import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import TwingateConnectForm from "./TwingateConnectForm";
import twingate_step_2_1 from "./images/twingate_step_2_1.png";
import twingate_step_2_2 from "./images/twingate_step_2_2.png";
import twingate_step_3 from "./images/twingate_step_3.png";

const TwingateIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const twingateLoginURL = "https://auth.twingate.com/";

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("twingate");
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerTwingateIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerTwingateIntegration(values.subdomain, values.apiToken)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Twingate integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Twingate and prioritize the suggested remediations."
            : ""
        }
        image={logos.TwingateLogo}
        name={"Twingate"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Twingate is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Twingate">
                  <Typography variant={"body2"}>
                    Log into your Twingate Admin Console
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(twingateLoginURL, "_blank");
                      }}
                    >
                      Open Twingate Login Page
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create a new API Token">
                  <Typography variant={"body2"}>
                    Navigate to Settings {">"} API {">"} Generate Token
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: twingate_step_2_1,
                        altext: "Twingate API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Give your token a label, make sure permission level "Read
                    only" is selected and click "Generate"
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: twingate_step_2_2,
                        altext: "Twingate Generate Token window",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Fill your Twingate subdomain and API token here">
                  <Box width={700}>
                    <Typography variant={"body2"}>
                      You can find your subdomain under Network {">"} Overview
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: twingate_step_3,
                          altext: "Twingate Generate Token window",
                        },
                      ]}
                      onExpanded={addToExpandedList}
                    />
                  </Box>
                  <Box width={700}>
                    <TwingateConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default TwingateIntegration;
